import { useState, useEffect, useRef, useCallback, type JSX } from 'react';
import { useNavigate } from 'react-router-dom';
import { SEO } from '../seo/SEO';
import { useI18n } from '../i18n';
import { useAuth } from '../auth';
import { AppHeader } from '../components/AppHeader';
import { useAppModal } from '../components/Modal';
import { GameGrid } from '../components/GameGrid';
import { Numpad } from '../components/Numpad';
import { SettingsPopup, type SettingToggle } from '../components/SettingsPopup';
import { FontSizeToggle } from '../components/FontSizeToggle';
import { Confetti } from '../components/Confetti';
import { useGameBoard, formatTime, isGiven, clearAutoNotes, toggleNoteLocal } from '../hooks/useGameBoard';
import { useGameEffects } from '../hooks/useGameEffects';
import { useScrollLock } from '../hooks/useScrollLock';
import { AchievementToast } from '../components/AchievementToast';
import { GameStatusBar } from '../components/GameStatusBar';
import { DIFF_COLORS } from '../theme/difficulty';
import { techniqueSlug, techniqueLevel, explainStep as explainStepText } from '@sudoku/shared';
import { AdBanner, AD_SLOTS } from '../components/AdBanner';
import { track, touchActivity } from '../lib/analytics';
import './DailyPage.css';
import './GamePage.css';

const API = import.meta.env.VITE_API_URL || 'http://localhost:8000';

// Ay/gün adları artık hardcoded dizi değil — Intl.DateTimeFormat ile locale'e
// göre otomatik üretiliyor (N-dil desteği, manuel çeviri gerekmiyor).
const LOCALE_TAG: Record<string, string> = {
  tr: 'tr-TR', en: 'en-US', nl: 'nl-NL', fr: 'fr-FR', de: 'de-DE',
  it: 'it-IT', pl: 'pl-PL', ru: 'ru-RU', es: 'es-ES',
};

function getMonthNames(locale: string): string[] {
  const tag = LOCALE_TAG[locale] ?? 'en-US';
  const fmt = new Intl.DateTimeFormat(tag, { month: 'long' });
  return Array.from({ length: 12 }, (_, i) => {
    const name = fmt.format(new Date(2024, i, 1));
    return name.charAt(0).toUpperCase() + name.slice(1);
  });
}

function getDayHeaders(locale: string): string[] {
  const tag = LOCALE_TAG[locale] ?? 'en-US';
  const fmt = new Intl.DateTimeFormat(tag, { weekday: 'short' });
  // 2024-01-01 Pazartesi'dir — Pt'den başlayan 7 günlük sıra için referans
  return Array.from({ length: 7 }, (_, i) => {
    const name = fmt.format(new Date(2024, 0, 1 + i));
    return name.charAt(0).toUpperCase() + name.slice(1, 2);
  });
}

interface PuzzleInfo {
  puzzle_id: string;
  difficulty: number;
  givens: number;
  status: 'not_started' | 'in_progress' | 'solved' | 'failed';
  score: number | null;
  elapsed_time: number | null;
}

interface CalendarDay {
  day: number;
  solved: number[];
  count: number;
}

interface LeaderboardRow {
  nickname: string;
  score: number;
  elapsed_time: number;
  mistakes: number;
}

export function DailyPage() {
  const navigate = useNavigate();
  const { t, locale } = useI18n();
  const { token, user } = useAuth();
  const { showConfirm, showAlert, showChoice, modalElement } = useAppModal();

  // Tab state
  const [tab, setTab] = useState<'puzzles' | 'calendar' | 'leaderboard'>('puzzles');

  // Puzzles tab
  const [puzzles, setPuzzles] = useState<PuzzleInfo[]>(() =>
    [1, 2, 3, 4, 5].map(diff => ({ puzzle_id: '', difficulty: diff, givens: 0, status: 'not_started' as const, score: null, elapsed_time: null }))
  );
  const [puzzleDate, _setPuzzleDate] = useState(() => todayStr());
  const [startingDiff, setStartingDiff] = useState<number | null>(null);

  // Calendar tab
  const [calYear, setCalYear] = useState(() => new Date().getFullYear());
  const [calMonth, setCalMonth] = useState(() => new Date().getMonth() + 1);
  const [calDays, setCalDays] = useState<CalendarDay[]>([]);

  // Leaderboard tab
  const [lbDiff, setLbDiff] = useState(3);
  const [lbRows, setLbRows] = useState<LeaderboardRow[]>([]);

  // Game state
  const [gameMode, setGameMode] = useState(false);
  const [attemptId, setAttemptId] = useState('');
  const [gameDiff, setGameDiff] = useState(3);
  const [gameFinished, setGameFinished] = useState(false);
  const [gameFailed, setGameFailed] = useState(false);
  const [newAchievements, setNewAchievements] = useState<string[]>([]);
  const [_loading, setLoading] = useState(false);
  const saveTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const fx = useGameEffects();

  // Product analytics deduplication sets
  const startedAttemptsRef = useRef(new Set<string>());
  const completedAttemptsRef = useRef(new Set<string>());
  const failedAttemptsRef = useRef(new Set<string>());

  // Shared board hook
  const placeValueRef = useRef<(r: number, c: number, value: number) => void>(() => {});
  const eraseRef = useRef<(r: number, c: number) => void>(() => {});
  const gb = useGameBoard({
    isPlaying: gameMode && !gameFinished && !gameFailed,
    onPlace: useCallback((r: number, c: number, v: number) => {
      touchActivity();
      placeValueRef.current(r, c, v);
    }, []),
    onErase: useCallback((r: number, c: number) => {
      touchActivity();
      eraseRef.current(r, c);
    }, []),
  });

  useScrollLock(!!gameMode);
  const headers = useCallback(() => {
    const h: Record<string, string> = { 'Content-Type': 'application/json' };
    if (token) h['Authorization'] = `Bearer ${token}`;
    return h;
  }, [token]);

  // Load status (puzzle üretmeden sadece durum bilgisi)
  useEffect(() => {
    if (!token || gameMode) return;
    loadStatus();
  }, [token, puzzleDate, gameMode]);

  // Load calendar
  useEffect(() => {
    if (!token || tab !== 'calendar') return;
    loadCalendar();
  }, [token, tab, calYear, calMonth]);

  // Load leaderboard
  useEffect(() => {
    if (!token || tab !== 'leaderboard') return;
    loadLeaderboard();
  }, [token, tab, lbDiff, puzzleDate]);

  // Auto-save time every 10s
  useEffect(() => {
    if (gameMode && !gameFinished && !gameFailed && attemptId) {
      saveTimerRef.current = setInterval(() => {
        fetch(`${API}/daily/save-time`, {
          method: 'POST',
          headers: headers(),
          body: JSON.stringify({ attempt_id: attemptId, elapsed_time: gb.elapsed }),
        }).catch(() => {});
      }, 10000);
    }
    return () => { if (saveTimerRef.current) clearInterval(saveTimerRef.current); };
  }, [gameMode, gameFinished, gameFailed, attemptId, gb.elapsed]);

  // Auth check — tüm hook'lardan SONRA olmalı
  if (!token || !user) {
    return (
      <div className="daily-page">
        <SEO path="/daily" />
        {modalElement}
        <AppHeader onBack={() => navigate('/')} backLabel={t.common.backToHome} title={t.daily.title} />
        <div className="daily-loading">{t.daily.loginRequired}</div>
      </div>
    );
  }

  async function loadStatus() {
    try {
      const res = await fetch(`${API}/daily/status?date=${puzzleDate}`, { headers: headers() });
      if (res.ok) {
        const data = await res.json();
        setPuzzles(data.puzzles);
      }
    } catch (err) { console.error(err); }
  }

  async function loadCalendar() {
    try {
      const res = await fetch(`${API}/daily/calendar?year=${calYear}&month=${calMonth}`, { headers: headers() });
      if (res.ok) {
        const data = await res.json();
        setCalDays(data.days);
      }
    } catch (err) { console.error(err); }
  }

  async function loadLeaderboard() {
    try {
      const res = await fetch(`${API}/daily/leaderboard?date=${puzzleDate}&difficulty=${lbDiff}`, { headers: headers() });
      if (res.ok) {
        const data = await res.json();
        setLbRows(data.leaderboard);
      }
    } catch (err) { console.error(err); }
  }

  // ─── Start game ────────────────────────────────────
  async function startGame(diff: number) {
    setLoading(true);
    setStartingDiff(diff);
    try {
      const res = await fetch(`${API}/daily/start`, {
        method: 'POST',
        headers: headers(),
        body: JSON.stringify({ date: puzzleDate, difficulty: diff }),
      });
      const data = await res.json();
      if (data.attempt_id) {
        setAttemptId(data.attempt_id);
        if (data.is_new === true && !startedAttemptsRef.current.has(data.attempt_id)) {
          startedAttemptsRef.current.add(data.attempt_id);
          track('game_start', { mode: 'daily', difficulty: data.difficulty }, { attempt_id: data.attempt_id });
        }
        gb.resetBoard(
          data.puzzle.map((r: any[]) => [...r]),
          data.board.map((r: any[]) => [...r]),
          data.notes.map((r: any[][]) => r.map((c: any[]) => [...c])),
          {
            elapsed: Math.round(data.elapsed_time || 0),
            mistakes: data.mistakes,
            hintsUsed: data.hints_used ?? 0,
            score: data.score || 0,
          },
        );
        setGameDiff(data.difficulty);
        setGameFinished(data.is_finished && (data.score ?? 0) > 0);
        setGameFailed(data.is_finished && (data.score ?? 0) === 0);
        setGameMode(true);
      }
    } catch (err) { console.error(err); }
    setLoading(false);
    setStartingDiff(null);
  }

  function exitGame() {
    if (attemptId && !gameFinished && !gameFailed) {
      fetch(`${API}/daily/save-time`, {
        method: 'POST',
        headers: headers(),
        body: JSON.stringify({ attempt_id: attemptId, elapsed_time: gb.elapsed }),
      }).catch(() => {});
    }
    if (gb.timerRef.current) clearInterval(gb.timerRef.current);
    if (saveTimerRef.current) clearInterval(saveTimerRef.current);
    setGameMode(false);
    loadStatus();
  }

  // ─── Game actions ──────────────────────────────────

  async function getHint() {
    if (gameFinished || gameFailed || gb.hintsUsed >= 3) return;
    try {
      const res = await fetch(`${API}/daily/hint`, {
        method: 'POST',
        headers: headers(),
        body: JSON.stringify({ attempt_id: attemptId }),
      });
      const data = await res.json();
      if (data.row !== undefined) {
        gb.setBoard(prev => { const b = prev.map(row => [...row]); b[data.row][data.col] = data.value; return b; });
        gb.setNotes(prev => clearAutoNotes(prev, data.row, data.col, data.value));
        gb.setHintsUsed(data.hints_used);
        gb.setSelectedCell([data.row, data.col]);
        track('hint_used', { mode: 'daily', difficulty: gameDiff }, { attempt_id: attemptId });
      }
    } catch (err) { console.error(err); }
  }

  // "Tek Adımı Açıkla": mevcut tahtadan bulunan ilk mantıksal adımı teknik+neden
  // ile gösterir. Aynı ipucu hakkını kullanır (getHint ile aynı gating).
  async function explainStep() {
    if (gameFinished || gameFailed || gb.hintsUsed >= 3) return;
    try {
      const res = await fetch(`${API}/daily/hint`, {
        method: 'POST',
        headers: headers(),
        body: JSON.stringify({ attempt_id: attemptId, explained: true }),
      });
      const data = await res.json();
      if (data.error) {
        await showAlert(
          data.error === 'hint_limit' ? t.game.hintLimit
            : data.error === 'no_step' ? t.game.explainedNoStep
            : data.error,
          t.game.hintTitle,
        );
        return;
      }
      if (data.applied) {
        gb.setBoard(prev => { const b = prev.map(row => [...row]); b[data.row][data.col] = data.value; return b; });
        gb.setNotes(prev => clearAutoNotes(prev, data.row, data.col, data.value));
        gb.setSelectedCell([data.row, data.col]);
      }
      gb.setHintsUsed(data.hints_used);
      track('hint_used', { mode: 'daily', difficulty: gameDiff }, { attempt_id: attemptId });

      const slug = techniqueSlug(data.technique);
      const level = techniqueLevel(data.technique);
      const localizedName = slug ? (t.edu.techniques as any)[slug]?.name : undefined;
      await showAlert(explainStepText(data, locale), `${localizedName ?? data.technique} · ${t.difficulties[level]}`);
    } catch (err) { console.error(err); }
  }

  async function openHintMenu() {
    if (gameFinished || gameFailed || gb.hintsUsed >= 3) return;
    const choice = await showChoice(
      (t.game as any).hintChoiceMsg,
      t.game.helpMenuTitle,
      t.game.explainStepBtn,
      t.game.hintBtn,
      t.common.cancel,
    );
    if (choice === 'confirm') { await explainStep(); return; }
    if (choice === 'extra') { await getHint(); }
  }

  // Adaylar modu (Approach A): canlı overlay, client-side (sunucuya gitmez,
  // manuel notları ezmez). Açınca not modu kapanır (karşılıklı dışlayan).
  function toggleCandidates() {
    const next = !gb.showCandidates;
    gb.setShowCandidates(next);
    if (next) gb.setNoteMode(false);
  }
  async function placeValue(_r: number, _c: number, value: number) {
    if (!gb.selectedCell || gameFinished || gameFailed || gb.isPaused) return;
    const [r, c] = gb.selectedCell;
    if (isGiven(gb.puzzle, r, c)) return;
    // Zaten doğru yerleştirilmiş hücreye aynı değer girilirse işlem yapma
    if (gb.board[r][c] !== null) return;

    if (gb.noteMode) {
      if (gb.showCandidates) return; // Adaylar açıkken not editleme kapalı (Approach A)
      // Optimistic local toggle — server yanıtını bekleme (sil→ekle yarışında
      // bayat yanıt eski notu geri getiriyordu).
      gb.setNotes(prev => toggleNoteLocal(prev, r, c, value));
      if (!gb.showCandidates) {
        // Fire-and-forget persist (resume için); yanıtı UI'a uygulama.
        fetch(`${API}/daily/note`, {
          method: 'POST',
          headers: headers(),
          body: JSON.stringify({ attempt_id: attemptId, row: r, col: c, value }),
        }).catch(() => {});
      }
      return;
    }

    try {
      const res = await fetch(`${API}/daily/place`, {
        method: 'POST',
        headers: headers(),
        body: JSON.stringify({ attempt_id: attemptId, row: r, col: c, value, elapsed_time: gb.elapsed }),
      });
      const data = await res.json();
      if (data.correct) {
        const updatedBoard = gb.board.map((row, rr) => row.map((cell, cc) => (rr === r && cc === c ? value : cell)));
        gb.setBoard(() => updatedBoard.map(row => [...row]));
        gb.setNotes(prev => {
          if (gb.autoCleanNotes) return clearAutoNotes(prev, r, c, value);
          const n = prev.map(row => row.map(cell => [...cell])); n[r][c] = []; return n;
        });
        fx.playCorrect();
        fx.vibrateCorrect();
        if (data.is_finished) {
          fx.triggerComplete(r, c);
          fx.playComplete();
          fx.vibrateComplete();
          setGameFinished(true);
          gb.setScore(data.score || 0);
          if (gb.timerRef.current) clearInterval(gb.timerRef.current);
          if (data.new_achievements?.length) setNewAchievements(data.new_achievements);
          if (!completedAttemptsRef.current.has(attemptId)) {
            completedAttemptsRef.current.add(attemptId);
            track('game_complete', { mode: 'daily', difficulty: gameDiff }, { attempt_id: attemptId, user: false });
          }
        } else {
          fx.triggerCorrect(r, c, updatedBoard, value);
        }
      } else {
        gb.setMistakes(data.mistakes);
        gb.setWrongCell([r, c]);
        setTimeout(() => gb.setWrongCell(null), 1000);
        fx.playWrong();
        fx.vibrateWrong();
        fx.triggerWrong(r, c);
        if (data.is_finished) {
          setGameFailed(true);
          if (gb.timerRef.current) clearInterval(gb.timerRef.current);
          if (!failedAttemptsRef.current.has(attemptId)) {
            failedAttemptsRef.current.add(attemptId);
            track('game_failed', { mode: 'daily', difficulty: gameDiff }, { attempt_id: attemptId, user: false });
          }
        }
      }
    } catch (err) { console.error(err); }
  }
  placeValueRef.current = placeValue;

  async function eraseCell(_r: number, _c: number) {
    if (!gb.selectedCell || gameFinished || gameFailed || gb.isPaused) return;
    const [r, c] = gb.selectedCell;
    if (isGiven(gb.puzzle, r, c)) return;
    try {
      await fetch(`${API}/daily/erase`, {
        method: 'POST',
        headers: headers(),
        body: JSON.stringify({ attempt_id: attemptId, row: r, col: c }),
      });
      gb.setBoard(prev => { const b = prev.map(row => [...row]); b[r][c] = null; return b; });
    } catch (err) { console.error(err); }
  }
  eraseRef.current = eraseCell;

  // Numpad handler — plain function to guarantee hook order integrity across auth hydration
  function handleNumpadClick(n: number) {
    gb.setHighlightDigit(n);
    placeValue(-1, -1, n);
  }

  // Settings
  const dailySettings: SettingToggle[] = [
    { key: 'autoClean', labelOn: t.game.autoCleanOn, labelOff: t.game.autoCleanOff, value: gb.autoCleanNotes, onToggle: () => gb.setAutoCleanNotes(v => !v) },
    { key: 'highlight', labelOn: t.game.highlightOn, labelOff: t.game.highlightOff, value: gb.highlightEnabled, onToggle: () => gb.setHighlightEnabled(v => !v) },
    { key: 'cellHighlight', labelOn: t.game.cellHighlightOn, labelOff: t.game.cellHighlightOff, value: gb.cellHighlightEnabled, onToggle: () => gb.setCellHighlightEnabled(v => !v) },
    { key: 'effects', labelOn: t.game.effectsOn, labelOff: t.game.effectsOff, value: fx.isEffectsEnabled, onToggle: fx.toggleEffects },
    { key: 'sound', labelOn: t.game.soundOn, labelOff: t.game.soundOff, value: fx.isSoundEnabled, onToggle: fx.toggleSound },
  ];

  // ─── Render: Game Mode ─────────────────────────────
  if (gameMode) {
    const isFinished = gameFinished || gameFailed;

    // ─── FINISHED ─────────────────────────────────────
    if (isFinished) {
      return (
        <div className="daily-page">
          {modalElement}
          <AppHeader
            onBack={exitGame}
            backLabel={t.daily.backToDaily}
            title={gameFinished ? t.game.congratulations : t.game.gameOver}
          />
          <div className="finish-screen">
            {gameFailed && <p className="game-over-msg">{t.daily.tooManyMistakes}</p>}
            <div className="finish-stats">
              <div className="stat score-stat">
                <span className="stat-label">{t.daily.score}</span>
                <span className="stat-value score-value">{gb.score}</span>
              </div>
              <div className="stat">
                <span className="stat-label">{t.game.duration}</span>
                <span className="stat-value">{formatTime(gb.elapsed)}</span>
              </div>
              <div className="stat">
                <span className="stat-label">{t.game.errorCount}</span>
                <span className="stat-value">{gb.mistakes}</span>
              </div>
              <div className="stat">
                <span className="stat-label">{t.game.hintCount}</span>
                <span className="stat-value">{gb.hintsUsed}</span>
              </div>
            </div>
            <div style={{ marginTop: 16 }}><AdBanner slot={AD_SLOTS.daily} /></div>
            <div className="finish-actions">
              <button onClick={exitGame}>{t.daily.backToDaily}</button>
            </div>
          </div>
        </div>
      );
    }

    // ─── PLAYING ──────────────────────────────────────
    return (
      <div className="daily-page">
        {modalElement}
        <AppHeader
          onBack={exitGame}
          backLabel={t.daily.backToDaily}
          showAuth={false}
          extraControls={<>
            <FontSizeToggle />
            <SettingsPopup settings={dailySettings} isOpen={gb.showSettings} onToggle={() => gb.setShowSettings(v => !v)} buttonLabel={t.room.settingsBtn} />
          </>}
        />
        <div className="game-page-body">
          <GameStatusBar
            elapsed={gb.elapsed}
            score={gb.score}
            scorePulsing={fx.scorePulsing}
            mistakes={gb.mistakes}
            maxMistakes={3}
            difficultyLabel={t.difficulties[gameDiff]}
            difficultyColor={DIFF_COLORS[gameDiff]}
            isPaused={gb.isPaused}
            onTogglePause={() => gb.setIsPaused(p => !p)}
          />
          <div className="game-layout">
            <div className="game-board-area">
              {fx.floatingScores.map(s => (
                <div
                  key={s.id}
                  className="float-score"
                  style={{ '--float-offset': `${s.offset}px`, '--float-row': String(s.row), '--float-col': String(s.col) } as React.CSSProperties}
                >{s.text}</div>
              ))}
              <Confetti active={fx.showConfetti} />
              <GameGrid
                board={gb.board}
                notes={gb.notes}
                showCandidates={gb.showCandidates}
                isPaused={gb.isPaused}
                getCellClass={(r, c) => { const base = gb.getCellClass(r, c); const e = fx.getCellEffectClass(r, c); return e ? `${base} ${e}` : base; }}
                getCellStyle={fx.getCellEffectStyle}
                onCellClick={gb.handleCellClick}
                onResumeFromPause={() => gb.setIsPaused(false)}
              />
              <Numpad board={gb.board} highlightDigit={gb.highlightDigit} onNumberClick={handleNumpadClick} className="solo-numpad-mobile" />
            </div>

            <div className="game-controls">
              <Numpad board={gb.board} highlightDigit={gb.highlightDigit} onNumberClick={handleNumpadClick} className="solo-numpad-desktop" />
              <div className="action-buttons">
                <div className="action-row action-row-play">
                  <button
                    className={`action-btn ${gb.noteMode ? 'active' : ''}`}
                    onClick={() => gb.setNoteMode(!gb.noteMode)}
                    disabled={gb.showCandidates}
                    title={gb.noteMode ? t.game.noteOn : t.game.noteOff}
                  >
                    <span className="btn-icon"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/></svg></span>
                    <span className="btn-label">{gb.noteMode ? t.game.noteOn : t.game.noteOff}</span>
                  </button>
                  <button className="action-btn" onClick={() => eraseCell(-1, -1)} title={t.game.eraseBtn}>
                    <span className="btn-icon"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 20H7L3 16l13-13 4 4-9.5 9.5"/><path d="M6 17l-3-3"/></svg></span>
                    <span className="btn-label">{t.game.eraseBtn}</span>
                  </button>
                  <button
                    className="action-btn hint-btn"
                    onClick={openHintMenu}
                    disabled={gb.hintsUsed >= 3}
                    title={t.daily.hintAffectsScore}
                  >
                    <span className="btn-icon"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg></span>
                    <span className="btn-label">{t.game.hintBtn}</span>
                    <span className="hint-badge">{3 - gb.hintsUsed}/3</span>
                  </button>
                  <button
                    className={`action-btn ${gb.showCandidates ? 'active' : ''}`}
                    onClick={toggleCandidates}
                    title={gb.showCandidates ? t.game.hideCandidates : t.game.showCandidates}
                  >
                    <span className="btn-icon"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></svg></span>
                    <span className="btn-label">{gb.showCandidates ? t.game.hideCandidates : t.game.showCandidates}</span>
                  </button>
                </div>
                <div className="action-row action-row-end">
                  <button
                    className="action-btn solver-link"
                    onClick={async () => {
                      const ok = await showConfirm(
                        t.game.analyzeWarningSolo,
                        t.game.analyzeWarningTitle,
                        t.game.analyzeConfirm,
                        t.common.cancel,
                      );
                      if (!ok) return;
                      const boardStr = gb.board.map(row => row.map(c => c ?? 0).join(',')).join(';');
                      navigate(`/solver?board=${encodeURIComponent(boardStr)}`);
                    }}
                    title={t.game.analyzeTooltip}
                  >
                    <span className="btn-icon"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg></span>
                    <span className="btn-label">{t.game.analyzeWithSolver}</span>
                  </button>
                </div>
              </div>
              {gb.hintsUsed > 0 && (
                <p className="daily-hint-note">{t.daily.hintAffectsScore}</p>
              )}
              <div className="game-play-ad"><AdBanner slot={AD_SLOTS.daily} /></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ─── Render: Main Page ─────────────────────────────
  const monthNames = getMonthNames(locale);
  const dayHeaders = getDayHeaders(locale);

  return (
    <div className="daily-page">
      {modalElement}
      <AchievementToast achievementIds={newAchievements} onDone={() => setNewAchievements([])} />
      <SEO path="/daily" />
      <AppHeader onBack={() => navigate('/')} backLabel={t.common.backToHome} title={t.daily.title} />
      {(() => {
        const solvedToday = puzzles.filter(p => p.status === 'solved').length;
        if (solvedToday === 0) return null;
        return (
          <div className="daily-streak-badge">
            {solvedToday === 5 ? '🏆' : '✅'} {t.daily.todayLabel}: {solvedToday}/5
          </div>
        );
      })()}
      <div className="daily-tabs">
          <button className={`daily-tab ${tab === 'puzzles' ? 'active' : ''}`} onClick={() => setTab('puzzles')}>
            {t.daily.todayPuzzles}
          </button>
          <button className={`daily-tab ${tab === 'calendar' ? 'active' : ''}`} onClick={() => setTab('calendar')}>
            {t.daily.calendar}
          </button>
          <button className={`daily-tab ${tab === 'leaderboard' ? 'active' : ''}`} onClick={() => setTab('leaderboard')}>
            {t.daily.leaderboard}
          </button>
        </div>

        {/* ─── Puzzles Tab — Timeline ─── */}
        {tab === 'puzzles' && (
          <div className="daily-timeline">
            {(() => {
              // İlk çözülmemiş bulmacayı "aktif" olarak işaretle (sıradaki).
              const firstUnsolvedIdx = puzzles.findIndex(p => p.status !== 'solved' && p.status !== 'failed');
              return puzzles.map((p, idx) => {
                const isStarting = startingDiff === p.difficulty;
                const disabled = isStarting || (!!startingDiff && startingDiff !== p.difficulty) || p.status === 'failed';
                const isActive = idx === firstUnsolvedIdx;
                const accent = DIFF_COLORS[p.difficulty];
                const stateClass =
                  p.status === 'solved' ? 'solved' :
                  p.status === 'failed' ? 'failed' :
                  p.status === 'in_progress' ? 'in_progress' :
                  isActive ? 'active' : 'pending';
                const elapsedTxt = p.elapsed_time != null
                  ? `${Math.floor(p.elapsed_time / 60)}:${String(Math.floor(p.elapsed_time % 60)).padStart(2, '0')}`
                  : null;
                const ctaLabel = isStarting
                  ? t.daily.generating
                  : p.status === 'solved'
                    ? t.daily.solvedBtn
                    : p.status === 'failed'
                      ? t.daily.failedLabel
                      : p.status === 'in_progress'
                        ? t.daily.continueBtn
                        : t.daily.startBtn;
                return (
                  <div key={p.difficulty} className={`daily-tl-row daily-tl-${stateClass}`}>
                    <div className="daily-tl-rail" aria-hidden="true">
                      <span
                        className="daily-tl-node"
                        style={{ ['--accent' as any]: accent }}
                      >
                        {p.status === 'solved' ? '✓' : p.status === 'failed' ? '✕' : <span className="daily-tl-node-dot" />}
                      </span>
                      {idx < puzzles.length - 1 && <span className="daily-tl-line" />}
                    </div>
                    <button
                      type="button"
                      className={`daily-tl-card daily-tl-card--${stateClass}`}
                      style={{ ['--accent' as any]: accent }}
                      onClick={() => !disabled && startGame(p.difficulty)}
                      disabled={disabled}
                    >
                      <div className="daily-tl-card-main">
                        <span className="daily-tl-bars" aria-hidden="true">
                          {[1, 2, 3, 4, 5].map(i => (
                            <span
                              key={i}
                              className={`daily-tl-bar daily-tl-bar--${i} ${i <= p.difficulty ? 'is-on' : ''}`}
                              style={i <= p.difficulty ? { background: accent } : undefined}
                            />
                          ))}
                        </span>
                        <span className="daily-tl-name">{t.difficulties[p.difficulty]}</span>
                        <div className="daily-tl-meta">
                          {p.status === 'solved' && p.score != null && (
                            <span className="daily-tl-chip daily-tl-chip--reward">⭐ {p.score}</span>
                          )}
                          {elapsedTxt && p.status !== 'failed' && (
                            <span className="daily-tl-chip">⏱ {elapsedTxt}</span>
                          )}
                          {p.status === 'in_progress' && (
                            <span className="daily-tl-chip daily-tl-chip--warn">{t.daily.inProgress}</span>
                          )}
                        </div>
                      </div>
                      <span className={`daily-tl-cta daily-tl-cta--${stateClass}`}>
                        {ctaLabel} {p.status !== 'solved' && p.status !== 'failed' ? '→' : ''}
                      </span>
                    </button>
                  </div>
                );
              });
            })()}
            {startingDiff && <div className="loading-msg">{t.daily.generating}</div>}
          </div>
        )}

        {/* ─── Calendar Tab ─── */}
        {tab === 'calendar' && (
          <div className="daily-calendar">
            <div className="daily-cal-nav">
              <button onClick={() => {
                if (calMonth === 1) { setCalMonth(12); setCalYear(y => y - 1); }
                else setCalMonth(m => m - 1);
              }}>{t.daily.prevMonth}</button>
              <span>{monthNames[calMonth - 1]} {calYear}</span>
              <button onClick={() => {
                const now = new Date();
                const nextM = calMonth === 12 ? 1 : calMonth + 1;
                const nextY = calMonth === 12 ? calYear + 1 : calYear;
                if (nextY < now.getFullYear() || (nextY === now.getFullYear() && nextM <= now.getMonth() + 1)) {
                  setCalMonth(nextM); setCalYear(nextY);
                }
              }}>{t.daily.nextMonth}</button>
            </div>

            <div className="daily-cal-grid">
              {dayHeaders.map(d => <div key={d} className="daily-cal-header">{d}</div>)}
              {renderCalendar(calYear, calMonth, calDays, (t.daily as any).calendarLevelsSolved)}
            </div>
          </div>
        )}

        {/* ─── Leaderboard Tab ─── */}
        {tab === 'leaderboard' && (
          <div className="daily-lb">
            <div className="daily-lb-filters">
              {[1,2,3,4,5].map(d => (
                <button
                  key={d}
                  className={`daily-lb-diff-btn ${lbDiff === d ? 'active' : ''}`}
                  style={lbDiff === d ? { background: DIFF_COLORS[d], borderColor: DIFF_COLORS[d] } : {}}
                  onClick={() => setLbDiff(d)}
                >
                  {t.difficulties[d]}
                </button>
              ))}
            </div>
            {lbRows.length === 0 ? (
              <div className="daily-lb-empty">{t.daily.noAttempts}</div>
            ) : (
              <table>
                <thead>
                  <tr>
                    <th>{t.daily.rank}</th>
                    <th>{t.daily.player}</th>
                    <th>{t.daily.score}</th>
                    <th>{t.common.time}</th>
                    <th>{t.common.mistakes}</th>
                  </tr>
                </thead>
                <tbody>
                  {lbRows.map((row, i) => (
                    <tr key={i}>
                      <td>{i + 1}</td>
                      <td>{row.nickname}</td>
                      <td style={{ fontWeight: 700, color: 'var(--btn-primary)' }}>{row.score}</td>
                      <td>{formatTime(Math.round(row.elapsed_time))}</td>
                      <td>{row.mistakes}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>
  );
}

// ─── Helpers ──────────────────────────────────────────

function todayStr() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function DayRing({ solved }: { solved: number[] }) {
  const allDone = solved.length === 5;
  const gap = 0.08;
  const DIFF_COLORS_RING: Record<number, string> = {
    1: '#5B8C5A', 2: '#5A7A8F', 3: '#8C7A3A', 4: '#8C5A44', 5: '#6B4A7A',
  };
  const cx = 50; const cy = 50; const r = 42; const stroke = 7;

  function arcPath(startA: number, endA: number) {
    const x1 = cx + r * Math.cos(startA);
    const y1 = cy + r * Math.sin(startA);
    const x2 = cx + r * Math.cos(endA);
    const y2 = cy + r * Math.sin(endA);
    const large = endA - startA > Math.PI ? 1 : 0;
    return `M ${x1} ${y1} A ${r} ${r} 0 ${large} 1 ${x2} ${y2}`;
  }

  return (
    <svg viewBox="0 0 100 100" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none' }}>
      <circle cx={cx} cy={cy} r={r} stroke="var(--border)" strokeWidth={stroke} fill="none" />
      {[1, 2, 3, 4, 5].map(d => {
        const startA = -Math.PI / 2 + ((d - 1) / 5) * Math.PI * 2 + gap / 2;
        const endA = -Math.PI / 2 + (d / 5) * Math.PI * 2 - gap / 2;
        const isOn = solved.includes(d);
        if (!isOn && !allDone) return null;
        return (
          <path
            key={d}
            d={arcPath(startA, endA)}
            stroke={allDone ? 'var(--success-text)' : DIFF_COLORS_RING[d]}
            strokeWidth={stroke}
            strokeLinecap="round"
            fill="none"
          />
        );
      })}
    </svg>
  );
}

function renderCalendar(year: number, month: number, calDays: CalendarDay[], levelsSolvedTpl: string) {
  const today = new Date();
  const todayDay = today.getFullYear() === year && today.getMonth() + 1 === month ? today.getDate() : -1;
  const firstDay = new Date(year, month - 1, 1).getDay();
  // Pazartesi=0 olacak şekilde offset
  const offset = (firstDay + 6) % 7;
  const daysInMonth = new Date(year, month, 0).getDate();

  const dayMap = new Map<number, CalendarDay>();
  calDays.forEach(d => dayMap.set(d.day, d));

  const isFuture = (day: number) => {
    const d = new Date(year, month - 1, day);
    const t = new Date(); t.setHours(0,0,0,0);
    return d > t;
  };

  const cells: JSX.Element[] = [];
  for (let i = 0; i < offset; i++) {
    cells.push(<div key={`e-${i}`} className="daily-cal-cell empty" />);
  }
  for (let day = 1; day <= daysInMonth; day++) {
    const info = dayMap.get(day);
    const count = info?.count ?? 0;
    const cls = [
      'daily-cal-cell',
      `solved-${Math.min(count, 5)}`,
      day === todayDay ? 'today' : '',
      isFuture(day) ? 'future' : '',
    ].filter(Boolean).join(' ');

    cells.push(
      <div key={day} className={cls} title={info ? levelsSolvedTpl.replace('{count}', String(count)) : ''}>
        {info && info.solved.length > 0 && <DayRing solved={info.solved} />}
        <span style={{ position: 'relative', zIndex: 1 }}>{day}</span>
      </div>
    );
  }
  return cells;
}

// ─── Mini Sudoku Board artık kullanılmıyor — game-grid ile değiştirildi

export default DailyPage;
